import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader, pct } from "../lib/ui";
import { isoWeek, weekOptions } from "../lib/week";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Copy, Activity, Trophy, Search, AlertTriangle, ListTodo, Printer, Presentation } from "lucide-react";

const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié dans le presse-papiers"); };

function downloadPptx(week, year) {
  const token = localStorage.getItem("token");
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dos-report/pptx?week=${week}&year=${year}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = `DOS_S${String(week).padStart(2, "0")}_${year}.pptx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export PowerPoint démarré.");
    });
}

function CopyCard({ title, icon: Icon, tone, children, copyText }) {
  const tones = { blue: "text-[#F05A28] bg-[#FFF3EE]", teal: "text-[#0284C7] bg-[#E0F2FE]", amber: "text-[#B45309] bg-[#FEF3C7]", red: "text-[#B91C1C] bg-[#FEE2E2]" };
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" /></div>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        </div>
        {copyText && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copy(copyText)} data-testid={`copy-${title}`}><Copy className="h-4 w-4 text-slate-400" /></Button>}
      </div>
      <div className="text-sm text-slate-600 space-y-1">{children}</div>
    </div>
  );
}

export default function DosReport() {
  const cur = isoWeek();
  const { t, lang } = useLang();
  const [week, setWeek] = useState(cur.week);
  const [year, setYear] = useState(cur.year);
  const { data, isLoading } = useQuery({
    queryKey: ["dos", week, year, lang],
    queryFn: async () => (await api.get("/dos-report", { params: { week, year, lang } })).data,
  });
  const c = data?.cards;

  return (
    <div data-testid="dos-page">
      <PageHeader title="Synthèse Hebdomadaire DOS" subtitle="Points clés prêts à copier pour le briefing exécutif">
        <Button size="sm" className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={() => downloadPptx(week, year)} data-testid="export-pptx"><Presentation className="h-4 w-4 mr-1" /> Export PowerPoint</Button>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" /> {t("Imprimer / PDF")}</Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm flex gap-3 items-end">
        <div><Label className="text-xs">{t("Semaine")}</Label>
          <Select value={String(week)} onValueChange={(v) => setWeek(Number(v))}>
            <SelectTrigger className="w-28" data-testid="dos-week"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{weekOptions().map((w) => <SelectItem key={w} value={String(w)}>S{String(w).padStart(2, "0")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t("Année")}</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="dos-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[cur.year, cur.year - 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">Chargement…</p> : c && (
        <>
          <div className="bg-[#F05A28] text-white rounded-md p-5 mb-5">
            <p className="text-xs uppercase tracking-wider text-white/70">{t("Aperçu")} · {t("S")}{String(data.week).padStart(2, "0")}/{data.year}</p>
            <p className="mt-1">{data.overview}</p>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="bg-white/15 px-3 py-1 rounded">{t("Complétude")}: <b>{pct(c.completeness)}</b></span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <CopyCard title={t("Activités de surveillance")} icon={Activity} tone="blue" copyText={c.surveillance_activities.map((a) => `${a.name}: ${a.count}`).join("\n")}>
              {c.surveillance_activities.map((a) => <p key={a.name}>• {a.name}: <b>{a.count}</b></p>)}
            </CopyCard>
            <CopyCard title={t("Réalisations clés")} icon={Trophy} tone="teal" copyText={c.achievements.join("\n")}>
              {c.achievements.length ? c.achievements.map((a, i) => <p key={i}>• {a}</p>) : <p className="text-slate-400">{t("Aucune")}</p>}
            </CopyCard>
            <CopyCard title={t("Constats clés")} icon={Search} tone="amber" copyText={c.key_findings.map((f) => `${f.category} (${f.count})`).join("\n")}>
              {c.key_findings.length ? c.key_findings.map((f) => <p key={f.category}>• {f.category} ({f.count})</p>) : <p className="text-slate-400">{t("Aucun")}</p>}
            </CopyCard>
            <CopyCard title={t("Défis majeurs")} icon={AlertTriangle} tone="red" copyText={c.major_challenges.map((f) => `${f.category} (${f.count})`).join("\n")}>
              {c.major_challenges.length ? c.major_challenges.map((f) => <p key={f.category}>• {f.category} · {f.count}</p>) : <p className="text-slate-400">{t("Aucun")}</p>}
            </CopyCard>
            <CopyCard title={t("Recommandations prioritaires")} icon={ListTodo} tone="red" copyText={c.priority_recommendations.map((r) => `[${r.priority}] ${r.recommendation}`).join("\n")}>
              {c.priority_recommendations.length ? c.priority_recommendations.map((r) => <p key={r.id}>• <b className="text-red-600">[{t(r.priority)}]</b> {r.recommendation}</p>) : <p className="text-slate-400">{t("Aucune")}</p>}
            </CopyCard>
            <CopyCard title={t("Évolution semaine sur semaine")} icon={Activity} tone="blue" copyText={c.week_over_week.join("\n")}>
              {c.week_over_week.length ? c.week_over_week.map((w, i) => <p key={i}>• {w}</p>) : <p className="text-slate-400">{t("Aucune variation significative")}</p>}
            </CopyCard>
          </div>
        </>
      )}
    </div>
  );
}
